import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { auth, config } from '../config';
import { AxiosError } from 'axios';
import { v4 } from 'uuid';
import { ApiLogger, logAPIEvent } from 'fmc-digital-common';

export enum ReadyStateScreen {
    LOAN_SUMMARY = 'loan_summary',
    LOAN_IN_PROGRESS = 'loan_in_progress'
}
type useReadyStateQueryProps = Partial<{
    enabled: boolean;
    screen: ReadyStateScreen;
    appId: string;
}>;

export const useReadyStateQuery = ({ enabled, screen, appId }: useReadyStateQueryProps): UseQueryResult<{ status: string }, unknown> => {
    const query = useQuery({
        queryKey: ['ready-state', screen],
        queryFn: async () => {
            return ApiLogger(
                {
                    method: 'get',
                    url: `${config.env('loanUrl')}/${appId}/ready-state?screen=${screen}`,
                    headers: {
                        'x-correlation-id': v4(),
                        Authorization: `Bearer ${auth.getOkta().getAccessToken()}`,
                        'x-api-key': config.env('xApiKey'),
                        source: 'DigiApp',
                        'Content-Type': 'application/json',
                        applicationname: 'servicing-portal'
                    }
                },
                logAPIEvent
            )
                .then(response => {
                    if (response.status !== 'Complete') {
                        const error: AxiosError = {
                            message: `${response.status} is not Complete`,
                            code: 'inProgress_response',
                            response: {
                                status: 202,
                                data: { status: 'InProgress' },
                                statusText: 'InProgress',
                                headers: {},
                                config: response.config
                            },
                            isAxiosError: true,
                            name: 'inProgress_response',
                            toJSON: () => ({ message: `${response.status} is not Complete` })
                        };
                        throw error;
                    }
                    return response;
                })
                .catch(err => {
                    if (err.response?.status) {
                        throw err;
                    }
                    const error: AxiosError = {
                        message: err,
                        response: {
                            status: 500,
                            data: { status: 'Error' },
                            statusText: 'api_error',
                            headers: null,
                            config: null
                        },
                        isAxiosError: true,
                        name: 'inProgress_response',
                        toJSON: () => err
                    };
                    throw error; // Rethrow the error to trigger retry logic
                });
        },
        enabled: enabled,
        select: ({ data }: any) => data,
        onSuccess: (result: any) => {
            return result;
        },
        onError: () => {
            console.log('final error not trying anymore');
        },
        retry: (failureCount, err: AxiosError) => {
            if (err.response?.status >= 400) {
                return false;
            }
            const numTries = screen === ReadyStateScreen.LOAN_SUMMARY ? 60 : 17;
            if (failureCount >= numTries) {
                return false;
            }
            return true;
        },
        retryDelay: screen === ReadyStateScreen.LOAN_SUMMARY ? 5000 : 10000,
        staleTime: Infinity
    });
    return query;
};
